/* ─── Textual value formatting ─── */

import type { ReactNode } from 'react';

/**
 * Format a "Textual" value (str | int | float | bool | list | None)
 * into a React node for display.
 */
export function formatTextual(value: unknown): ReactNode {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number') return formatNumber(value);
  if (Array.isArray(value)) return formatList(value);
  return String(value);
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Number.isInteger(n)) {
    return n.toLocaleString('en-US');
  }
  // Scientific notation for very large or very small
  const abs = Math.abs(n);
  if (abs !== 0 && (abs >= 1e9 || abs < 1e-4)) {
    return n.toExponential(3);
  }
  // Up to 4 significant digits, strip trailing zeros
  return parseFloat(n.toPrecision(4)).toLocaleString('en-US', {
    maximumFractionDigits: 6,
  });
}

function formatList(items: unknown[]): ReactNode {
  const parts: ReactNode[] = [];
  for (let i = 0; i < items.length; i++) {
    if (i > 0) {
      parts.push(
        <span key={`sep-${i}`} style={{ color: 'var(--artifact-text-tertiary, #999)' }}>
          ,{' '}
        </span>,
      );
    }
    parts.push(<span key={i}>{formatTextual(items[i])}</span>);
  }
  return <>{parts}</>;
}
